import React, { useState, useEffect, useCallback, useMemo } from 'react';
import BlockPlaceEffect from './BlockPlaceEffect';
import HardDropTrail from './HardDropTrail';
import LineClearEffect from './LineClearEffect';
import TetrisFinisher from './TetrisFinisher';
import ScoreEffect from './ScoreEffect';
import { getBlockColor } from '../materials/SpectrumMaterial';
import { getTheme } from '../themes';

const EffectManager = ({ onlineGame, isLocalPlayer, skinName = 'Prime' }) => {
  const [effects, setEffects] = useState([]);

  // Get themed effects based on skinName
  const themedEffects = useMemo(() => {
    const theme = getTheme(skinName);
    return {
      BlockPlaceEffect: theme.effects?.BlockPlaceEffect || BlockPlaceEffect,
      HardDropTrail: theme.effects?.HardDropTrail || HardDropTrail,
      LineClearEffect: theme.effects?.LineClearEffect || LineClearEffect,
      TetrisFinisher: theme.effects?.TetrisFinisher || TetrisFinisher,
      getBlockColor: theme.getBlockColor || getBlockColor,
    };
  }, [skinName]);

  const addEffect = useCallback((type, data) => {
    const id = Date.now() + Math.random();
    setEffects(prev => [...prev, { id, type, data }]);
  }, []);

  const removeEffect = useCallback((id) => {
    setEffects(prev => prev.filter(e => e.id !== id));
  }, []);

  // Subscribe to game effect events
  // Works for both local player (OnlineGame) and opponents (OpponentSimulator)
  useEffect(() => {
    if (!onlineGame) return;

    // Hard drop trail - smooth jet-stream wind effect
    const unsubHardDrop = onlineGame.onEffect?.('hardDrop', (data) => {
      addEffect('hardDrop', data);
    });

    // Subscribe to block place events
    const unsubBlockPlace = onlineGame.onEffect?.('blockPlace', (data) => {
      addEffect('blockPlace', {
        position: data.position,
        color: themedEffects.getBlockColor(data.colorClass)
      });
    });

    // Subscribe to line clear events
    const unsubLineClear = onlineGame.onEffect?.('lineClear', (data) => {
      addEffect('lineClear', data);
    });

    // Subscribe to tetris (4-line) events
    const unsubTetris = onlineGame.onEffect?.('tetris', (data) => {
      addEffect('tetris', data);
    });

    // Subscribe to score animation events
    const unsubScore = onlineGame.onEffect?.('scoreAnimation', (data) => {
      addEffect('scoreAnimation', {
        rows: data.rows,
        score: data.score,
        spectrumColor: data.colorClass ? themedEffects.getBlockColor(data.colorClass) : null,
      });
    });

    return () => {
      unsubHardDrop?.();
      unsubBlockPlace?.();
      unsubLineClear?.();
      unsubTetris?.();
      unsubScore?.();
    };
  }, [onlineGame, isLocalPlayer, addEffect, themedEffects]);

  // Get themed effect components
  const {
    BlockPlaceEffect: ThemedBlockPlaceEffect,
    HardDropTrail: ThemedHardDropTrail,
    LineClearEffect: ThemedLineClearEffect,
    TetrisFinisher: ThemedTetrisFinisher
  } = themedEffects;

  return (
    <>
      {effects.map(effect => {
        switch (effect.type) {
          case 'blockPlace':
            return (
              <ThemedBlockPlaceEffect
                key={effect.id}
                position={effect.data.position}
                color={effect.data.color}
                onComplete={() => removeEffect(effect.id)}
              />
            );

          case 'hardDrop':
            return (
              <ThemedHardDropTrail
                key={effect.id}
                startY={effect.data.startY}
                endY={effect.data.endY}
                columns={effect.data.columns}
                onComplete={() => removeEffect(effect.id)}
              />
            );

          case 'lineClear':
            return (
              <ThemedLineClearEffect
                key={effect.id}
                rows={effect.data.rows}
                linesCleared={effect.data.linesCleared}
                onComplete={() => removeEffect(effect.id)}
              />
            );

          case 'tetris':
            return (
              <ThemedTetrisFinisher
                key={effect.id}
                blocks={effect.data.blocks}
                onComplete={() => removeEffect(effect.id)}
              />
            );

          case 'scoreAnimation':
            return (
              <ScoreEffect
                key={effect.id}
                rows={effect.data.rows}
                score={effect.data.score}
                skinName={skinName}
                spectrumColor={effect.data.spectrumColor}
                onComplete={() => removeEffect(effect.id)}
              />
            );

          default:
            return null;
        }
      })}
    </>
  );
};

export default EffectManager;
